import { useMemo } from 'react';

import { Box, Stack, Button, Checkbox, Typography, FormControlLabel } from '@mui/material';

import { useTranslate } from 'src/locales';

import { ExportGroup, ExportTarget, EXPORT_GROUP_ORDER } from './export-fields';

type Props = {
  targets: ExportTarget[];
  selected: Set<string>;
  /** Sets each listed column on or off. */
  onChange: (ids: string[], checked: boolean) => void;
};

/** Import groups reuse the importer's headings, so the two screens name a section the same way. */
const groupLabelKey = (group: ExportGroup): string =>
  group === 'profile' || group === 'record' ? `data_export.groups.${group}` : `csv_import.groups.${group}`;

export default function ExportFieldPicker({ targets, selected, onChange }: Props) {
  const { t } = useTranslate();

  const groups = useMemo(
    () =>
      EXPORT_GROUP_ORDER.map((group) => ({ group, targets: targets.filter((target) => target.group === group) })).filter(
        (entry) => entry.targets.length > 0
      ),
    [targets]
  );

  const allIds = useMemo(() => targets.map((target) => target.id), [targets]);
  const selectedCount = allIds.filter((id) => selected.has(id)).length;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2">
          {t('data_export.fields_selected', { count: selectedCount, total: allIds.length })}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => onChange(allIds, true)} disabled={selectedCount === allIds.length}>
            {t('data_export.select_all')}
          </Button>
          <Button size="small" onClick={() => onChange(allIds, false)} disabled={selectedCount === 0}>
            {t('data_export.clear')}
          </Button>
        </Stack>
      </Stack>

      {groups.map(({ group, targets: groupTargets }) => {
        const ids = groupTargets.map((target) => target.id);
        const ticked = ids.filter((id) => selected.has(id)).length;
        const label = t(groupLabelKey(group));

        return (
          <Box key={group} role="group" aria-label={label}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={ticked === ids.length}
                  indeterminate={ticked > 0 && ticked < ids.length}
                  onChange={(event) => onChange(ids, event.target.checked)}
                  inputProps={{ 'aria-label': t('data_export.select_group', { group: label }) }}
                />
              }
              label={
                <Typography variant="subtitle2">
                  {label}{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {t('data_export.group_count', { count: ticked, total: ids.length })}
                  </Typography>
                </Typography>
              }
            />

            <Box
              sx={{
                pl: 3.5,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              }}
            >
              {groupTargets.map((target) => (
                <FormControlLabel
                  key={target.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.has(target.id)}
                      onChange={(event) => onChange([target.id], event.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">{target.label}</Typography>}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}
